import { useEffect, useState } from 'react';
import { api, ErroApi, lerContexto, type Chamado } from '../api';
import {
  Campo, Carregando, Erro, Modal, Selo, Tabela, Vazio,
} from '../componentes/ui';

const STATUS: Record<Chamado['status'], string> = {
  aberto: 'atencao', em_atendimento: 'info', resolvido: 'ok', cancelado: 'inativo',
};
const dataHora = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/**
 * O chamado é a única porta que a softhouse tem pra corrigir dado de
 * terceiro: sem um chamado em atendimento e dentro do prazo, quem atende
 * suporte não enxerga empresa nenhuma. Esta tela serve os dois lados —
 * quem abre (contador/cliente) e quem atende (suporte) — porque o que
 * muda é só a ação disponível, não o conceito.
 */
export function Chamados() {
  const [dados, setDados] = useState<{ chamados: Chamado[]; souSuporte: boolean } | null>(null);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [novo, setNovo] = useState(false);
  const souCliente = lerContexto()?.tipo === 'empresa';

  async function carregar() {
    try {
      setDados(await api.chamados());
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar os chamados.');
    }
  }
  useEffect(() => { void carregar(); }, []);

  async function agir(fn: () => Promise<unknown>) {
    setErro('');
    setOcupado(true);
    try {
      await fn();
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui concluir.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Chamados</h1>
          <p>
            {dados?.souSuporte
              ? 'Chamados abertos esperando atendimento. Atender dá acesso temporário só àquela empresa, por 48 horas.'
              : 'Achou algo errado numa nota ou num dado? Abra um chamado — é assim que a softhouse corrige, nunca sem avisar.'}
          </p>
        </div>
        {dados && !dados.souSuporte && (
          <button className="btn btn-primary btn-sm" onClick={() => setNovo(true)}>Novo chamado</button>
        )}
      </header>

      <Erro>{erro}</Erro>

      {!dados ? <Carregando /> : dados.chamados.length === 0 ? (
        <Vazio
          titulo="Nada por aqui"
          descricao={dados.souSuporte ? 'Quando um chamado for aberto, aparece aqui.' : 'Seus chamados aparecem aqui depois de abertos.'}
        />
      ) : (
        <Tabela
          itens={dados.chamados}
          chaveDe={(c) => c.id}
          colunas={[
            { rotulo: 'Empresa', principal: true, celula: (c) => <strong>{c.empresa?.razaoSocial ?? c.escritorio}</strong> },
            { rotulo: 'Descrição', fraco: true, celula: (c) => c.descricao },
            ...(dados.souSuporte
              ? [{ rotulo: 'Aberto por', fraco: true, celula: (c: Chamado) => c.abertoPor?.nome ?? '—' }]
              : []),
            { rotulo: 'Aberto em', fraco: true, celula: (c) => dataHora(c.criadoEm) },
            {
              rotulo: dados.souSuporte ? 'Acesso até' : 'Atendido por',
              fraco: true,
              celula: (c) => (dados.souSuporte
                ? (c.acessoAte ? dataHora(c.acessoAte) : '—')
                : (c.atendidoPor?.nome ?? '—')),
            },
            { rotulo: 'Status', celula: (c) => <Selo tipo={STATUS[c.status]} texto={c.status.replace('_', ' ')} /> },
            {
              rotulo: 'Ações',
              acoes: true,
              celula: (c) => (
                <>
                  {dados.souSuporte && c.status === 'aberto' && (
                    <button className="link-botao" disabled={ocupado} onClick={() => agir(() => api.atenderChamado(c.id))}>Atender</button>
                  )}
                  {dados.souSuporte && c.status === 'em_atendimento' && (
                    <button className="link-botao" disabled={ocupado} onClick={() => agir(() => api.resolverChamado(c.id))}>Resolver</button>
                  )}
                  {!dados.souSuporte && c.status === 'aberto' && (
                    <button className="link-botao" disabled={ocupado} onClick={() => agir(() => api.cancelarChamado(c.id))}>Cancelar</button>
                  )}
                </>
              ),
            },
          ]}
        />
      )}

      {novo && (
        <NovoChamado
          empresaFixa={souCliente ? lerContexto()?.id : undefined}
          onFechar={() => setNovo(false)}
          onCriado={() => { setNovo(false); void carregar(); }}
        />
      )}
    </div>
  );
}

function NovoChamado({
  empresaFixa, onFechar, onCriado,
}: { empresaFixa?: number; onFechar: () => void; onCriado: () => void }) {
  const [empresas, setEmpresas] = useState<{ id: number; razaoSocial: string }[] | null>(null);
  const [empresaId, setEmpresaId] = useState<number | ''>(empresaFixa ?? '');
  const [descricao, setDescricao] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (empresaFixa) return;
    api.leituraEmpresas().then((r) => setEmpresas(r.empresas)).catch(() => setEmpresas([]));
  }, [empresaFixa]);

  async function salvar() {
    if (!empresaId) { setErro('Escolha a empresa.'); return; }
    setErro('');
    setSalvando(true);
    try {
      await api.abrirChamado({ empresaId, descricao });
      onCriado();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui abrir o chamado.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal titulo="Novo chamado" descricao="Conte o que está errado — quanto mais específico, mais rápido resolve." onFechar={onFechar}>
      <Erro>{erro}</Erro>
      {!empresaFixa && (
        <Campo rotulo="Empresa">
          {!empresas ? <Carregando /> : (
            <select className="input" value={empresaId} onChange={(e) => setEmpresaId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Selecione…</option>
              {empresas.map((e) => <option key={e.id} value={e.id}>{e.razaoSocial}</option>)}
            </select>
          )}
        </Campo>
      )}
      <Campo rotulo="O que está acontecendo" ajuda="Pelo menos 10 caracteres.">
        <textarea className="input" value={descricao} onChange={(e) => setDescricao(e.target.value)}
          placeholder="Ex.: a nota 1234 de 03/10 não aparece na lista de documentos" autoFocus />
      </Campo>
      <div className="modal-acoes">
        <button className="btn btn-secondary" onClick={onFechar}>Cancelar</button>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando || !empresaId || descricao.trim().length < 10}>
          {salvando ? <span className="spinner" /> : 'Abrir chamado'}
        </button>
      </div>
    </Modal>
  );
}
